import { Button, Paper } from '@mui/material';
import type { Feature, Polygon } from "geojson";
import { GeoJsonObject } from "geojson";
import L from 'leaflet';
import markerGreen2x from 'leaflet-color-markers/img/marker-icon-2x-green.png';
import markerGreen from 'leaflet-color-markers/img/marker-icon-green.png';
import markerBlue2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerBlue from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoJSON, LayersControl, MapContainer, Marker, Popup, TileLayer, useMap, WMSTileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.Default.css';
import { useNavigate } from "react-router";
import { getCadastralParcelMapLayer, getGeologicalMapLayer, getMilitaryMapLayer, getSoilMapLayer, getWaterBodiesLayer } from '../../api/captures/captureLocation';
import { useErrorHandler } from '../../hooks/Error/useErrorHandler';
import { FeatureCollection } from "../../types/model/captureLocation";

const defaultCenter: [number, number] = [39.5, -8]; // fallback (Portugal)

const emptyFeatureCollection: GeoJsonObject = {
  type: "FeatureCollection",
  features: [],
} as GeoJsonObject;

const DefaultIcon = L.icon({
  iconRetinaUrl: markerBlue2x,
  iconUrl: markerBlue,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const SelectedIcon = L.icon({
  iconRetinaUrl: markerGreen2x,
  iconUrl: markerGreen,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface CaptureLocationMapProps {
  geojson?: FeatureCollection;
  isMaximized?: boolean;
  selectedCaptures: number[];
  showViewDetails?: boolean;
  entityId?: number;
  managementUnitId?: number;
  captureId?: number;
  ellipse?: Feature<Polygon> | null;
}

interface FitToDataProps {
  geojson?: FeatureCollection;
  ellipse?: Feature<Polygon> | null;
  defaultCenter: [number, number];
  defaultZoom: number;
  isMaximized?: boolean;
}

const FitToData = ({ geojson, ellipse, defaultCenter, defaultZoom, isMaximized }: FitToDataProps) => {
  const map = useMap();
  const isMaximizedRef = useRef(isMaximized);

  useEffect(() => {
    isMaximizedRef.current = isMaximized;

    if (ellipse) {
      const coords = ellipse.geometry.coordinates[0] as [number, number][];
      const bounds = L.latLngBounds(coords.map(([lng, lat]) => [lat, lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
      return;
    }

    const fitBounds = () => {
      if (!geojson?.features || geojson.features.length === 0) {
        map.setView(defaultCenter, defaultZoom);
        return;
      }

      let bbox: string | number[] | undefined = geojson.bbox;
      if (typeof bbox === "string") {
        const match = new RegExp(/BOX\(([-\d.]+)\s+([-\d.]+),([-\d.]+)\s+([-\d.]+)\)/).exec(bbox);
        if (match) {
          bbox = [
            Number.parseFloat(match[1]),
            Number.parseFloat(match[2]),
            Number.parseFloat(match[3]),
            Number.parseFloat(match[4]),
          ];
        }
      }

      if (
        Array.isArray(bbox) &&
        bbox.length === 4 &&
        bbox.every((n) => typeof n === "number" && !Number.isNaN(n))
      ) {
        const bounds: L.LatLngBoundsExpression = [
          [bbox[1], bbox[0]], // SW
          [bbox[3], bbox[2]], // NE
        ];
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    };

    const timer = setTimeout(fitBounds, 400);
    return () => clearTimeout(timer);
  }, [geojson, ellipse, map, defaultCenter, defaultZoom, isMaximized]);

  return null;
};

const MapResizer = ({ isMaximized }: { isMaximized?: boolean }) => {
  const map = useMap();
  useEffect(() => {
    const interval = setInterval(() => {
      map.invalidateSize();
    }, 10); // Invalidate size every 10ms for smoother transition

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 400);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isMaximized, map]);
  return null;
};

async function fetchGeoJsonLayer(
  fetchFunction: () => Promise<any>,
  setState: (data: GeoJsonObject) => void,
  handleError: (error: unknown) => void
) {
  try {
    const data = await fetchFunction();

    let bbox: number[] | undefined;

    if (typeof data.bbox === "string") {
      const match = data.bbox.match(
        /BOX\(([-\d.]+)\s+([-\d.]+),([-\d.]+)\s+([-\d.]+)\)/
      );
      if (match) {
        bbox = [
          Number.parseFloat(match[1]),
          Number.parseFloat(match[2]),
          Number.parseFloat(match[3]),
          Number.parseFloat(match[4]),
        ];
      }
    } else if (Array.isArray(data.bbox)) {
      bbox = data.bbox;
    }

    const bbox6 =
      bbox?.length === 4
        ? [bbox[0], bbox[1], 0, bbox[2], bbox[3], 0]
        : undefined;

    setState({
      ...data,
      bbox: bbox6,
    });
  } catch (error) {
    handleError(error);
  }
}

// Mantém o GeoJSON sempre montado (nunca muda de posição no LayersControl)
// e atualiza o conteúdo diretamente no layer do Leaflet via ref, sem remount.
type StableGeoJsonLayerProps = {
  data?: GeoJsonObject;
  style: L.PathOptions;
  onEachFeature: (feature: any, layer: L.Layer) => void;
};

const StableGeoJsonLayer = ({ data, style, onEachFeature }: StableGeoJsonLayerProps) => {
  const layerRef = useRef<L.GeoJSON>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    layer.clearLayers();
    if (data) {
      layer.addData(data as any);
    }
  }, [data]);

  return (
    <GeoJSON
      ref={layerRef}
      data={emptyFeatureCollection as any}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
};

function CaptureLocationMapBase({ geojson, isMaximized, selectedCaptures, showViewDetails = true, entityId, managementUnitId, captureId, ellipse }: Readonly<CaptureLocationMapProps>) {
  const defaultZoom = 6;
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();
  const mapRef = useRef<L.Map>(null);

  const [militaryMapsData, setMilitaryMapsData] = useState<GeoJsonObject>();
  const [geologicalMapData, setGeologicalMapData] = useState<GeoJsonObject>();
  const [soilMapData, setSoilMapData] = useState<GeoJsonObject>();
  const [waterBodiesData, setWaterBodiesData] = useState<GeoJsonObject>();
  const [cadastralParcelMapData, setCadastralParcelMapData] = useState<GeoJsonObject>();

  useEffect(() => {
    fetchGeoJsonLayer(getMilitaryMapLayer, setMilitaryMapsData, handleError);
    fetchGeoJsonLayer(getGeologicalMapLayer, setGeologicalMapData, handleError);
    fetchGeoJsonLayer(getSoilMapLayer, setSoilMapData, handleError);
    fetchGeoJsonLayer(getWaterBodiesLayer, setWaterBodiesData, handleError);
  }, []);

  useEffect(() => {
    fetchGeoJsonLayer(
      () => getCadastralParcelMapLayer({ entityId, managementUnitId, captureId }),
      setCadastralParcelMapData,
      handleError
    );
  }, [entityId, managementUnitId, captureId]);


  const containerStyle: React.CSSProperties = {
    position: 'relative',
    height: isMaximized ? "calc(100vh - 122px)" : "500px",
    width: '100%',
    transition: 'height 0.3s ease-in-out',
  };

  return (
    <Paper sx={{p: 1}}>
      <div style={containerStyle}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.Overlay name={t('captures.map.orthophoto')}>
              <WMSTileLayer
                updateWhenIdle={true}
                updateWhenZooming={false}
                url="https://ortos.dgterritorio.gov.pt/wms/ortosat2023?"
                layers="ortoSat2023-CorVerdadeira"
                format="image/png"
                transparent={true}
                version="1.3.0"
                tileSize={128}
                attribution='OrtoSat2023 © DGT'
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.cadastral-parcel')}>
              <WMSTileLayer
                updateWhenIdle={true}
                updateWhenZooming={false}
                url="https://snicws.dgterritorio.gov.pt/geoserver/inspire/ows?"
                layers="cadastralparcel"
                format="image/png"
                transparent={true}
                version="1.3.0"
                tileSize={128}
                attribution='Cadastro Predial © DGT'
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.my-cadastral-parcels')}>
              <StableGeoJsonLayer
                data={cadastralParcelMapData}
                style={{ color: 'purple', weight: 2, fillOpacity: 0.1 }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties) {
                    layer.bindPopup(`<strong>${t('captures.map.cadastral-parcel')}</strong> ${feature.properties.name}`);
                  }
                }}
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.military-maps')}>
              <StableGeoJsonLayer
                data={militaryMapsData}
                style={{ color: 'green', weight: 2, fillOpacity: 0.1 }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties) {
                    layer.bindPopup(`<strong>${t('captures.map.military-map-label')}</strong> ${feature.properties.mapNumber}`);
                  }
                }}
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.geological-map')}>
              <StableGeoJsonLayer
                data={geologicalMapData}
                style={{ color: 'orange', weight: 2, fillOpacity: 0.1 }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties) {
                    layer.bindPopup(`<strong>${t('captures.map.geological-map-label')}</strong> ${feature.properties.div50}`);
                  }
                }}
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.soil-map')}>
              <StableGeoJsonLayer
                data={soilMapData}
                style={{ color: 'brown', weight: 2, fillOpacity: 0.1 }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties) {
                    layer.bindPopup(`<strong>${t('captures.map.soil-map-label')}</strong> ${feature.properties.code}`);
                  }
                }}
              />
            </LayersControl.Overlay>

            <LayersControl.Overlay name={t('captures.map.water-bodies')}>
              <StableGeoJsonLayer
                data={waterBodiesData}
                style={{ color: 'blue', weight: 2, fillOpacity: 0.1 }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties) {
                    layer.bindPopup(`<strong>${t('captures.map.water-bodies-label')}</strong> ${feature.properties.name}`);
                  }
                }}
              />
            </LayersControl.Overlay>
          </LayersControl>

          {geojson?.features && (
            <MarkerClusterGroup
              chunkedLoading
              removeOutsideVisibleBounds
            >
              {[...geojson.features]
                .sort((a, b) => {
                  const nameA = a.properties.name ?? "";
                  const nameB = b.properties.name ?? "";
                  return nameA.localeCompare(nameB);
                })
                .map((feature) => {
                  const { name, entityDesignation, entityName, managementUnitName, captureId } = feature.properties;
                  const { coordinates } = feature.geometry;
                  const position: [number, number] = [coordinates[1], coordinates[0]];
                  const isSelected = selectedCaptures.includes(captureId);
                  const icon = isSelected ? SelectedIcon : DefaultIcon;
                  const captureName = name || t("captures.map.unnamed-capture");

                  return (
                    <Marker key={captureId} position={position} icon={icon}>
                      <Popup minWidth={250} autoPan={false} keepInView={false} closeButton={true}>
                        <div>
                          <strong>{captureName}</strong><br/>
                          <strong>{t("captures.map.entity-desig")} {entityDesignation}</strong><br/>
                          <br/>
                          {t("captures.map.entity")} {entityName?.toUpperCase()}<br/>
                          {t("captures.map.management-unit")} {managementUnitName}<br/>
                          {t("captures.map.longitude")} {coordinates[0]}<br/>
                          {t("captures.map.latitude")} {coordinates[1]}<br/>
                          {showViewDetails && (
                            <div style={{ textAlign: "center", marginTop: "10px" }}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/captures/${captureId}`);
                                }}
                              >
                                {t("generic.view-details")}
                              </Button>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
            </MarkerClusterGroup>
          )}

          {ellipse && (
            <GeoJSON
              key={JSON.stringify(ellipse)}
              data={ellipse as any}
              style={{
                color: "red",
                weight: 2,
                dashArray: "1 6",
                fillColor: "red",
                fillOpacity: 0.15,
              }}
            />
          )}
          <FitToData 
            geojson={geojson}
            ellipse={ellipse}
            defaultCenter={defaultCenter} 
            defaultZoom={defaultZoom} 
            isMaximized={isMaximized}
          />
          <MapResizer isMaximized={isMaximized} />
        </MapContainer>
      </div>
    </Paper>
  );
};

// Prevents re-rendering unless props change
const CaptureLocationMap = React.memo(CaptureLocationMapBase);
export default CaptureLocationMap;
